// Enhanced notification system composable
import { useEventBus } from '@/composables/useEventBus'

export function useNotifications() {
  const { emit } = useEventBus()

  // Notification types with their default configurations
  const notificationTypes = {
    success: {
      color: 'success',
      icon: 'mdi-check-circle',
      timeout: 4000
    },
    error: {
      color: 'error', 
      icon: 'mdi-alert-circle',
      timeout: 6000
    },
    warning: {
      color: 'warning',
      icon: 'mdi-alert',
      timeout: 5000
    },
    info: {
      color: 'info',
      icon: 'mdi-information',
      timeout: 4000
    }
  }

  // Show notification with enhanced options
  const showNotification = ({
    message,
    type = 'info',
    title = null,
    timeout = null,
    persistent = false,
    actions = null,
    details = null
  }) => {
    const config = notificationTypes[type] || notificationTypes.info
    
    emit('show-notification', {
      message,
      title,
      type,
      color: config.color,
      icon: config.icon,
      timeout: persistent ? 0 : (timeout || config.timeout),
      persistent,
      actions,
      details,
      timestamp: new Date().toISOString()
    })
  }

  // Convenience methods for different notification types
  const showSuccess = (message, options = {}) => {
    showNotification({ message, type: 'success', ...options })
  }

  const showError = (message, options = {}) => {
    showNotification({ message, type: 'error', ...options })
  }

  const showWarning = (message, options = {}) => {
    showNotification({ message, type: 'warning', ...options })
  }

  const showInfo = (message, options = {}) => {
    showNotification({ message, type: 'info', ...options })
  }

  // Show operation success with details
  const showOperationSuccess = (operation, details = null) => {
    showSuccess(`${operation} completed successfully`, {
      title: 'Operation Successful',
      details
    })
  }

  // Show operation error with details
  const showOperationError = (operation, error, details = null) => {
    const errorMessage = error?.message || error || 'An unexpected error occurred'
    showError(`Failed to ${operation.toLowerCase()}: ${errorMessage}`, {
      title: 'Operation Failed',
      details,
      persistent: true
    })
  }

  // Show validation errors
  const showValidationErrors = (errors) => {
    if (Array.isArray(errors)) {
      errors.forEach(error => showError(error))
    } else if (typeof errors === 'object') {
      Object.values(errors).forEach(error => {
        if (Array.isArray(error)) {
          error.forEach(err => showError(err))
        } else {
          showError(error)
        }
      })
    } else {
      showError(errors)
    }
  }

  // Show meeting-related notifications
  const showMeetingNotification = (action, meetingTitle, meetingDate = null) => {
    const title = `Meeting ${action}`
    let message = `"${meetingTitle}" has been ${action.toLowerCase()}`
    
    if (meetingDate) {
      message += ` for ${meetingDate}`
    }

    const type = ['cancelled', 'deleted', 'ended'].includes(action.toLowerCase()) ? 'warning' : 'success'
    
    showNotification({
      message,
      title,
      type,
      details: meetingDate ? `Scheduled: ${meetingDate}` : null
    })
  }

  // Show attendance notifications
  const showAttendanceNotification = (action, memberName = null, meetingTitle = null) => {
    let message
    if (action === 'confirmed') {
      message = memberName ? `${memberName}'s attendance confirmed` : 'Attendance confirmed'
    } else if (action === 'checked-in') {
      message = memberName ? `${memberName} checked in` : 'Successfully checked in'
    } else {
      message = `Attendance ${action}`
    }

    showSuccess(message, {
      title: 'Attendance Update',
      details: meetingTitle ? `Meeting: ${meetingTitle}` : null
    })
  }

  // Show document notifications  
  const showDocumentNotification = (action, documentTitle, category = null) => {
    const message = `Document "${documentTitle}" ${action.toLowerCase()}`
    const title = `Document ${action}`
    
    showSuccess(message, {
      title,
      details: category ? `Category: ${category}` : null
    })
  }

  return {
    showNotification,
    showSuccess,
    showError, 
    showWarning,
    showInfo,
    showOperationSuccess,
    showOperationError,
    showValidationErrors,
    showMeetingNotification,
    showAttendanceNotification,
    showDocumentNotification
  }
}